import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import * as React from 'react';

import {
  SURFACE_PADDING,
  PADDING_X,
  PADDING_Y,
  PADDING_TOP,
  MARGIN_X,
  SIZE_SCALE,
  GAP_SCALE,
  COLOR_SCALE,
  TYPE_SCALE,
  RADIUS_SCALE,
} from '@/components/design-system/tokens';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      `flex h-full w-full flex-col overflow-hidden ${RADIUS_SCALE.md} ${COLOR_SCALE.surface} ${COLOR_SCALE.foreground}`,
      className
    )}
    {...props}
  />
));
Command.displayName = CommandPrimitive.displayName;

interface CommandDialogProps extends React.ComponentProps<typeof Dialog> {
  children: React.ReactNode;
}

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className={`overflow-hidden ${SURFACE_PADDING.none}`}>
        <Command
          className={`[&_[cmdk-group-heading]]:${PADDING_X['2']} [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:${PADDING_TOP['0']} [&_[cmdk-group]]:${PADDING_X['2']} [&_[cmdk-input-wrapper]_svg]:${SIZE_SCALE['5']} [&_[cmdk-input]]:${SIZE_SCALE['12']} [&_[cmdk-item]]:${PADDING_X['2']} [&_[cmdk-item]]:${PADDING_Y['3']} [&_[cmdk-item]_svg]:${SIZE_SCALE['5']}`}
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div className={`flex items-center border-b ${PADDING_X['3']}`} cmdk-input-wrapper="">
    <Search
      className={`${MARGIN_X['2']} ${SIZE_SCALE['4']} ${SIZE_SCALE.w4} shrink-0 opacity-50`}
    />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        `flex ${SIZE_SCALE['10']} w-full ${RADIUS_SCALE.md} bg-transparent ${PADDING_Y['3']} ${TYPE_SCALE.sm} outline-none placeholder:${COLOR_SCALE['foreground-muted']} disabled:cursor-not-allowed disabled:opacity-50`,
        className
      )}
      {...props}
    />
  </div>
));

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn(
      'max-h-[var(--component-max-height-md)] overflow-y-auto overflow-x-hidden',
      className
    )}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className={`${PADDING_Y['6']} text-center ${TYPE_SCALE.sm}`}
    {...props}
  />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      `overflow-hidden ${SURFACE_PADDING['1']} ${COLOR_SCALE.foreground} [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:${TYPE_SCALE['2xs']} [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:${COLOR_SCALE['foreground-muted']}`,
      className
    )}
    {...props}
  />
));

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn(`${MARGIN_X['1']} border-t ${COLOR_SCALE.border}`, className)}
    {...props}
  />
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      `relative flex cursor-default ${GAP_SCALE.sm} select-none items-center ${RADIUS_SCALE.sm} ${PADDING_X['2']} ${PADDING_Y['1.5']} ${TYPE_SCALE.sm} outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:${SIZE_SCALE['4']} [&_svg]:shrink-0`,
      className
    )}
    {...props}
  />
));

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        `left-auto ${TYPE_SCALE['2xs']} tracking-widest ${COLOR_SCALE['foreground-muted']}`,
        className
      )}
      {...props}
    />
  )
);
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
